import React from 'react';
import { Button, Result, Space } from 'antd';
// Desde 'umi' y no '@umijs/max': este componente lo carga app.tsx durante el
// arranque, ver el comentario en src/utils/errorRedirect.ts.
import { history } from 'umi';

import { describeError } from '@/utils/globalErrorHandlers';

import image from '@/assets/error-B.svg';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  /**
   * Mensaje técnico del error. Se muestra discreto en pantalla porque en QA y
   * producción no hay source maps (`DEVTOOL: false`): el stack de la consola
   * queda minificado y solo el mensaje sigue siendo legible. Así el usuario
   * puede sacar una captura y el detalle viaja con ella al pedir soporte.
   */
  errorMessage: string;
}

/**
 * Atrapa las excepciones que ocurren **durante el render** de cualquier
 * componente de la app y muestra una pantalla amable en lugar de dejar el
 * árbol desmontado, que es lo que hace React por defecto y se ve como una
 * página en blanco.
 *
 * No cubre los errores lanzados dentro de manejadores de eventos ni en código
 * asíncrono: React no los propaga hasta acá. De esos se encarga
 * `registerGlobalErrorHandlers` en src/utils/globalErrorHandlers.ts.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  private unlisten?: () => void;

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: describeError(error) };
  }

  componentDidMount() {
    // React no reinicia un boundary por su cuenta: una vez que atrapó un error
    // se queda mostrando el fallback para siempre. Sin esto, después de un
    // crash el usuario vería la pantalla de error incluso al navegar a otra
    // sección desde el menú.
    this.unlisten = history.listen(() => {
      if (this.state.hasError) {
        this.setState({ hasError: false, errorMessage: '' });
      }
    });
  }

  componentWillUnmount() {
    if (this.unlisten) this.unlisten();
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Error de render no controlado:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Result
        title="Algo no funcionó como esperábamos"
        style={{ background: 'none', paddingTop: 80 }}
        icon={<img src={image} alt="Error" height={250} />}
        subTitle={
          <>
            Tuvimos un problema al mostrar esta pantalla. Podés volver e intentarlo de nuevo.
            {this.state.errorMessage && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: 'rgba(0, 0, 0, 0.45)',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.errorMessage}
              </div>
            )}
          </>
        }
        extra={
          <Space>
            <Button onClick={() => history.back()}>Volver</Button>
            <Button type="primary" onClick={() => history.push('/')}>
              Volver al inicio
            </Button>
          </Space>
        }
      />
    );
  }
}

export default ErrorBoundary;
